/**
 * Page Utilities
 *
 * Helpers for the JSON-based page management system.
 * - mergePages: per-key shallow merge of page data maps, sorted by position
 * - resolvePageTokens: converts a ResolvedPage into a PageComposition
 * - resolveTemplate: replaces {{field}} tokens with property values
 * - expandTemplatePages: expand template pages into concrete pages (delegates to page-expand.mjs)
 */

import type { PageComposition, PageSection } from './types';
// @ts-ignore — pure JS shared module (no types)
import { expandTemplatePages as _expandTemplatePages } from './page-expand.mjs';

// ============================================================================
// Types
// ============================================================================

/**
 * Template types for corporate sites. Template pages are instantiated
 * once per data record during SSG (e.g. one page per property).
 */
export type PageTemplate = 'property' | 'state' | 'city' | 'hyperlocal';

// ============================================================================
// Page Context Dimensions
// ============================================================================

/**
 * A named data dimension that a page template may require.
 * Each dimension produces a selectable collection in the editor.
 */
export type ContextDimension = 'property' | 'hyperlocal';

/**
 * Maps each template type to the ordered context dimensions it requires.
 * The editor reads this to decide which selectors to show for the current page.
 *
 * Empty array = the template is an aggregate/listing page (state, city) that
 * derives its content from the full dataset rather than a single selected item.
 */
export const PAGE_CONTEXT_REQUIREMENTS: Record<PageTemplate, ContextDimension[]> = {
  state:      [],
  city:       [],
  property:   ['property'],
  hyperlocal: ['property', 'hyperlocal'],
};

/**
 * Raw page data as stored in pages.json.
 * All fields except sections are optional so child layers can provide partial overrides.
 */
export interface PageDataJSON {
  name?: string;
  path?: string;
  position?: number;
  seo_title?: string;
  seo_description?: string;
  visible?: boolean;
  /** When true, page gets <meta name="robots" content="noindex, nofollow" /> and is excluded from sitemap. */
  noindex?: boolean;
  /** ISO timestamp. When set, the page is unpublished (editor, SSG, SPA). `null` clears a parent tombstone. */
  deleted_at?: string | null;
  sections?: PageSection[];
  /** When set, this page is a template that gets expanded at build time. */
  template?: PageTemplate;
}

/**
 * An object keyed by page alias, as stored in pages.json.
 */
export type PageDataMap = Record<string, Partial<PageDataJSON>>;

/**
 * A fully resolved page with alias attached.
 */
export interface ResolvedPage {
  alias: string;
  name: string;
  path: string;
  position: number;
  seo_title?: string;
  seo_description?: string;
  visible: boolean;
  noindex: boolean;
  deleted_at?: string | null;
  sections: PageSection[];
  /** When set, this is a template page expanded at build time. */
  template?: PageTemplate;
}

// ============================================================================
// Merge
// ============================================================================

/**
 * A page with `deleted_at` set is unpublished. Live surfaces drop it after merge.
 * `null` / missing / empty clears a parent tombstone.
 */
export function isActivePage(page: { deleted_at?: string | null } | null | undefined): boolean {
  return !page?.deleted_at;
}

/**
 * Merge one or more page data maps (base + override layers).
 * Each layer is per-key shallow merged on top of the previous.
 * Returns a sorted array of resolved pages (sorted ascending by position).
 * Deleted pages are kept so the editor can list them; live maps filter with isActivePage.
 */
export function mergePages(...layers: PageDataMap[]): ResolvedPage[] {
  const merged: Record<string, Partial<PageDataJSON>> = {};

  for (const layer of layers) {
    for (const [alias, data] of Object.entries(layer)) {
      merged[alias] = { ...merged[alias], ...data };
    }
  }

  return Object.entries(merged)
    .map(([alias, data]) => ({
      alias,
      name: data.name ?? alias,
      path: data.path ?? `/${alias}`,
      position: data.position ?? 0,
      seo_title: data.seo_title,
      seo_description: data.seo_description,
      visible: data.visible ?? true,
      noindex: data.noindex ?? false,
      deleted_at: data.deleted_at || null,
      sections: data.sections ?? [],
      template: data.template,
    }))
    .sort((a, b) => a.position - b.position);
}

// ============================================================================
// Token Resolution
// ============================================================================

/**
 * The one token grammar: what resolveTemplate replaces, what the editor's
 * combobox inserts, and what the schema linter validates. Field name captured.
 *
 * NOTE: global regex — safe with String.replace/matchAll (both reset or clone
 * state), but do not use with .test()/.exec(), which share lastIndex.
 */
export const PAGE_TOKEN_RE = /\{\{(\w+)\}\}/g;

/**
 * Replace {{field_name}} tokens in a template string with values from a property object.
 */
export function resolveTemplate(template: string | undefined, property: Record<string, any>): string {
  if (!template) return '';
  return template.replace(PAGE_TOKEN_RE, (_, field) => property?.[field] ?? '');
}

// ============================================================================
// Page Field Validation
// ============================================================================
//
// Shared by the editor UI (page settings / add-page dialogs) and the schema
// linter (pages.json rule), so a page the AI editor writes passes exactly the
// same checks a user goes through in the modal. Each returns a human-readable
// error message, or null when valid.

/**
 * Validate a page's display name.
 */
export function validatePageName(name: string): string | null {
  if (!name.trim()) return 'Page name is required';
  if (name.length > 120) return 'Page name is too long (max 120 characters)';
  return null;
}

/**
 * Validate a page URL path. Paths may contain `{{token}}` placeholders
 * (template pages use e.g. `/{{state_slug}}/{{city_slug}}`), so tokens are
 * stripped before the character-set check. No dots: every real page path is
 * kebab-case route segments, so a period is always a typo.
 */
export function validatePagePath(path: string): string | null {
  const trimmed = path.trim();
  if (!trimmed) return 'URL path is required';
  if (/\s/.test(trimmed)) return 'Path cannot contain spaces';
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed) || trimmed.includes('://')) {
    return 'Enter a relative path (e.g. /amenities), not a full URL';
  }
  if (!trimmed.startsWith('/')) return 'Path must start with /';
  if (trimmed.includes('//')) return 'Path cannot contain consecutive slashes';
  if (/[?#]/.test(trimmed)) return 'Path cannot contain ? or #';

  const withoutTokens = trimmed.replace(PAGE_TOKEN_RE, 'x');
  if (/[{}]/.test(withoutTokens)) {
    return 'Malformed {{token}} placeholder';
  }
  if (!/^\/[A-Za-z0-9\-_/]*$/.test(withoutTokens)) {
    return 'Only letters, numbers, hyphens, underscores, and slashes are allowed';
  }
  return null;
}

/**
 * Flag `{{ ... }}` usages that resolveTemplate would NOT replace (spaces
 * inside the braces, unclosed braces, etc.).
 */
export function validateTemplateTokens(template: string): string | null {
  const stripped = template.replace(PAGE_TOKEN_RE, '');
  if (stripped.includes('{{') || stripped.includes('}}')) {
    return 'Malformed {{field}} token — use {{field_name}} with no spaces inside the braces';
  }
  return null;
}

/**
 * Convert a ResolvedPage into a PageComposition.
 * SEO title/description become functions that resolve {{tokens}} against property data.
 */
export function resolvePageTokens(page: ResolvedPage): PageComposition {
  return {
    alias: page.alias,
    path: page.path,
    title: (property) => resolveTemplate(page.seo_title, property as Record<string, any>),
    description: (property) => resolveTemplate(page.seo_description, property as Record<string, any>),
    noindex: page.noindex,
    visible: page.visible,
    sections: page.sections,
    template: page.template,
  };
}

/**
 * Build the list of page paths that are valid cross-link targets.
 *
 * Shared single source of truth for the blurb cross-link verifier's
 * `availablePaths`. Both the SSG (ssg-entry.ts) and the per-site client
 * `main.tsx` call THIS helper over their respective page sets, guaranteeing
 * they compute an identical filtered list and so agree on every link's
 * keep/unwrap decision (avoiding a React hydration mismatch — #425).
 *
 * A page is a valid cross-link target when it has a non-empty string `path`,
 * is published (`isActivePage`), is not hidden (`visible === false` is
 * excluded; missing/undefined → visible), and is indexable (`noindex === true`
 * is excluded; missing → indexable).
 */
export function crossLinkPagePaths(
  pages: ReadonlyArray<{ path?: string; visible?: boolean; noindex?: boolean; deleted_at?: string | null }>,
): string[] {
  return pages
    .filter(
      (p) =>
        typeof p.path === 'string' &&
        p.path.length > 0 &&
        isActivePage(p) &&
        p.visible !== false &&
        p.noindex !== true,
    )
    .map((p) => p.path as string);
}

// ============================================================================
// Template Expansion (delegates to page-expand.mjs — single source of truth)
// ============================================================================

interface StateData {
  name: string;
  abbr: string;
  slug: string;
  cities: Record<string, CityData>;
}

interface CityData {
  name: string;
  slug: string;
  properties: Record<string, any>[];
}

type PropertiesByState = Record<string, StateData>;

/**
 * Expand template pages into concrete pages using the company's property index.
 *
 * Template pages use `{{token}}` placeholders in their `path`, `name`,
 * `seo_title`, etc. Static (non-template) pages pass through unchanged.
 *
 * @returns An expanded array of ResolvedPages with unique aliases.
 */
export function expandTemplatePages(
  resolvedPages: ResolvedPage[],
  propertiesByState: PropertiesByState,
  _allProperties: Record<string, any>[],
): ResolvedPage[] {
  return _expandTemplatePages(resolvedPages, propertiesByState) as ResolvedPage[];
}
